import React from 'react';
import axios from 'axios';
import {
  useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { translateInline } from '../i18n/inlineTranslations';
import { normalizeLanguage } from '../i18n/languages';
import AnalysisTabBar from '../components/AnalysisTabBar';
import { useChart } from '../contexts/ChartContext';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { buildChakraPayload } from '../utils/chakraPayload';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const getChakraLevel = (value: number) => (value >= 25 ? 'strong' : value >= 14 ? 'balanced' : 'low');
const getTatvaLevel = (value: number) => (value >= 30 ? 'dominant' : value >= 15 ? 'balanced' : 'low');

const CHAKRA_INTERPRETATIONS_EN: Record<string, Record<string, string>> = {
  muladhara: {
    strong: 'Your grounding and foundation energy appears strong in this chart.',
    balanced: 'Your grounding energy appears generally balanced in this chart.',
    low: 'Reflection on routine, discipline, and inner stability may be useful.',
  },
  swadhisthana: {
    strong: 'Your creative and emotional flow energy appears strong in this chart.',
    balanced: 'Your creative energy appears balanced in this chart.',
    low: 'Reflection on creativity, emotional expression, and inner flow may be useful.',
  },
  manipura: {
    strong: 'Your confidence and willpower energy appears strong in this chart.',
    balanced: 'Your action-taking energy appears balanced in this chart.',
    low: 'Reflection on confidence, discipline, and decision power may be useful.',
  },
  anahata: {
    strong: 'Your connection and compassion energy appears strong in this chart.',
    balanced: 'Your emotional connection energy appears balanced in this chart.',
    low: 'Reflection on harmony, forgiveness, and emotional balance may be useful.',
  },
  vishuddha: {
    strong: 'Your communication and expression energy appears strong in this chart.',
    balanced: 'Your expression energy appears balanced in this chart.',
    low: 'Reflection on clear communication and self-expression may be useful.',
  },
  ajna: {
    strong: 'Your intuition and inner clarity appears strong in this chart.',
    balanced: 'Your intuitive clarity appears balanced in this chart.',
    low: 'Reflection on focus, intuition, and inner guidance may be useful.',
  },
};

const TATVA_INTERPRETATIONS_EN: Record<string, Record<string, string>> = {
  fire: {
    dominant: 'A strong Fire element supports action, courage, and leadership energy.',
    balanced: 'Fire appears balanced, supporting a steady rhythm between action and patience.',
    low: 'Reflection on confidence and action-taking may be useful.',
  },
  earth: {
    dominant: 'A strong Earth element supports practicality, patience, and stability.',
    balanced: 'Earth appears balanced, supporting practical thinking with flexibility.',
    low: 'Reflection on grounding, consistency, and planning may be useful.',
  },
  air: {
    dominant: 'A strong Air element supports ideas, communication, and adaptability.',
    balanced: 'Air appears balanced, supporting harmony between thinking and action.',
    low: 'Reflection on communication and flexibility may be useful.',
  },
  water: {
    dominant: 'A strong Water element supports intuition, sensitivity, and emotional depth.',
    balanced: 'Water appears balanced, supporting harmony between emotions and logic.',
    low: 'Reflection on emotional expression and inner connection may be useful.',
  },
  ether: {
    dominant: 'A strong Ether element supports spiritual awareness and broader vision.',
    balanced: 'Ether appears balanced, supporting harmony between spiritual and practical approach.',
    low: 'Reflection, silence, and spiritual discipline may be useful.',
  },
};

const DEVATA_EN: Record<string, { devata: string; meaning: string; note: string }> = {
  Sun: {
    devata: 'Surya Narayan / Lord Shiva',
    meaning: 'For inner confidence, clarity, and soul strength.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Moon: {
    devata: 'Lord Shiva / Devi Parvati',
    meaning: 'For calmness, emotional balance, and softness.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Mars: {
    devata: 'Hanuman Ji / Lord Kartikeya',
    meaning: 'For courage, protection, and discipline.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Mercury: {
    devata: 'Lord Ganesha / Lord Vishnu',
    meaning: 'For intellect, speech, learning, and obstacle removal.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Jupiter: {
    devata: 'Lord Vishnu / Brihaspati Dev',
    meaning: 'For knowledge, guidance, dharma, and blessings.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Venus: {
    devata: 'Mahalakshmi Ji',
    meaning: 'For harmony, beauty, comfort, and prosperity.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Saturn: {
    devata: 'Shani Dev / Hanuman Ji',
    meaning: 'For karma, patience, discipline, and protection.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Rahu: {
    devata: 'Maa Durga / Bhairav',
    meaning: 'For protection from confusion, fear, and illusion.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
  Ketu: {
    devata: 'Lord Ganesha',
    meaning: 'For spiritual clarity, detachment, and obstacle removal.',
    note: 'This Ishta Devata indication is based on planetary strength, lagna lord, moon sign lord, and atmakaraka from the birth chart.',
  },
};

const ValueBar = ({ value, color }: { value: number; color: string }) => (
  <LinearProgress
    variant="determinate"
    value={Math.max(0, Math.min(100, value))}
    sx={{
      height: 10,
      borderRadius: 999,
      bgcolor: "var(--app-surface-neutral, #edf2ef)",
      '& .MuiLinearProgress-bar': {
        bgcolor: color,
        borderRadius: 999,
      },
    }}
  />
);

const ChakraAnalysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentChart } = useChart();
  const { state: authState } = useAuth();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const isHindi = language === 'hi';
  const isPremium = hasPremiumSubscription(authState.user);
  const [chartData, setChartData] = React.useState<any>(null);
  const [chakraData, setChakraData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showPremiumLock, setShowPremiumLock] = React.useState(false);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess({
    onShowPremiumLock: () => setShowPremiumLock(true)
  });

  const generateChakra = React.useCallback(async (chart: any) => {
    if (!chart) return;
    const payload = { ...buildChakraPayload(chart), language };
    await checkFeatureAccess({
      featureKey: 'CHAKRA_INSIGHTS',
      apiCall: (trialAccessToken?: string) => axios.post('/kundli/chakra', payload, {
        headers: trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : undefined
      }),
      onSuccess: (response: any) => {
        setShowPremiumLock(false);
        setChakraData(response.data);
      }
    });
  }, [checkFeatureAccess, language]);

  React.useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const chart = id === 'preview'
          ? currentChart
          : (await axios.get(`${API_BASE_URL}/charts/${id}`)).data.data.chart;

        if (!chart) throw new Error('Chart data is required for Chakra insight.');

        setChartData(chart);
        setChakraData(null);
        if (isPremium) await generateChakra(chart);
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || 'Unable to load Chakra insight.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentChart, id, isPremium, isHindi, authState.token, generateChakra]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !chakraData) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        {id && <AnalysisTabBar chartId={id} active="chakra" isHoraryChart={chartData?.chartType === 'horary'} />}
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--app-ink-primary)", mb: 1 }}>Chakra Insights</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Chakra Insights is a plan feature. Free users can use 1 premium trial credit to generate this result.
            </Typography>
            {showPremiumLock && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your 4 free trial access are used. Upgrade to Kundlistar Premium for unlimited premium access.
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={() => generateChakra(chartData)}
              disabled={!chartData || trialLoading}
            >
              Generate Chakra Insight
            </Button>
          </Paper>
        )}
        <PremiumTrialPopup
          open={popup.open}
          featureName={popup.featureName}
          used={popup.used}
          total={popup.total}
          remaining={popup.remaining}
          loading={trialLoading}
          onCancel={closePopup}
          onContinue={popup.nextAction}
        />
      </Container>
    );
  }

  const { chakraBalance, tatvaBalance, ishtaDevata } = chakraData.sections;
  const strongestChakra = chakraData.summary?.strongestChakra;
  const weakestChakra = chakraData.summary?.weakestChakra;
  const dominantTatva = chakraData.summary?.dominantTatva;
  const devataEn = DEVATA_EN[ishtaDevata.planet] || null;
  const summaryText = chakraData.summary?.text || `According to the planets in your birth chart, ${strongestChakra?.name || 'one chakra'} energy appears most active, while ${weakestChakra?.name || 'one chakra'} may need more balancing reflection. Your dominant element appears to be ${dominantTatva?.name || 'an element'}.`;
  const labels = isHindi
    ? {
      subtitle: 'कुंडली आधारित आध्यात्मिक ऊर्जा संकेत',
      source: 'यह विश्लेषण आपकी व्यक्तिगत जन्म कुंडली के ग्रहों के आधार पर है।',
      summary: 'सारांश',
      chakraBalance: 'चक्र संतुलन',
      tatvaBalance: 'पंच तत्व संतुलन',
      ishtaDevata: 'इष्ट देवता',
      signalPlanet: 'संकेत ग्रह',
      mantra: 'मंत्र',
      note: 'नोट',
      disclaimer: 'यह केवल जन्म कुंडली आधारित आध्यात्मिक energy insight और self-reflection है। यह professional advice नहीं है।',
    }
    : {
      subtitle: 'Spiritual energy insights from Kundli',
      source: 'Calculated from your individual birth chart',
      summary: 'Summary',
      chakraBalance: 'Chakra Balance',
      tatvaBalance: 'Panch Tatva Balance',
      ishtaDevata: 'Ishta Devata',
      signalPlanet: 'Signal planet',
      mantra: 'Mantra',
      note: 'Note',
      disclaimer: chakraData.disclaimer,
    };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {id && <AnalysisTabBar chartId={id} active="chakra" isHoraryChart={chartData?.chartType === 'horary'} />}

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, bgcolor: "var(--app-surface-neutral, #f6fbf7)" }}>
        <Typography variant="h3" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>Chakra</Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
          {labels.subtitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {labels.source}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>{labels.summary}</Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{summaryText}</Typography>
      </Paper>

      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>{labels.chakraBalance}</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {chakraBalance.map((chakra: any) => (
          <Grid size={{ xs: 12, md: 6 }} key={chakra.key}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: chakra.color }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{isHindi ? chakra.hindi : chakra.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{isHindi ? chakra.name : chakra.hindi}</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{chakra.value}%</Typography>
              </Stack>
              <ValueBar value={chakra.value} color={chakra.color} />
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
                {chakra.keywords.map((keyword: string) => <Chip key={keyword} size="small" label={`#${translateInline(language, keyword)}`} />)}
              </Stack>
              <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                {language === 'en'
                  ? CHAKRA_INTERPRETATIONS_EN[chakra.key]?.[getChakraLevel(chakra.value)] || chakra.interpretation
                  : chakra.interpretation}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>{labels.tatvaBalance}</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {tatvaBalance.map((tatva: any) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tatva.key}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: tatva.color }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{isHindi ? tatva.hindi : tatva.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{isHindi ? tatva.name : tatva.hindi}</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{tatva.value}%</Typography>
              </Stack>
              <ValueBar value={tatva.value} color={tatva.color} />
              <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                {language === 'en'
                  ? TATVA_INTERPRETATIONS_EN[tatva.key]?.[getTatvaLevel(tatva.value)] || tatva.interpretation
                  : tatva.interpretation}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>{labels.ishtaDevata}</Typography>
      <Paper sx={{ p: 2.5, mb: 3, bgcolor: "var(--app-surface-orange, #fff8e1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--app-ink-neutral, #5D4037)" }}>
          {isHindi ? ishtaDevata.devata : devataEn?.devata || ishtaDevata.devata}
        </Typography>
        {ishtaDevata.planetHindi && (
          <Typography variant="body2" sx={{ mt: 0.5, color: "var(--app-ink-orange, #6D4C41)", fontWeight: 600 }}>
            {labels.signalPlanet}: {isHindi ? ishtaDevata.planetHindi : ishtaDevata.planet}
          </Typography>
        )}
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "var(--app-surface-neutral, #fff)" }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--app-ink-orange, #8D6E63)" }}>{labels.mantra}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--app-ink-neutral, #3E2723)" }}>{ishtaDevata.mantra}</Typography>
        </Paper>
        <Typography sx={{ mt: 2, fontWeight: 600, lineHeight: 1.8 }}>
          {isHindi ? ishtaDevata.meaning : devataEn?.meaning || ishtaDevata.meaning}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "var(--app-ink-orange, #795548)", lineHeight: 1.7 }}>
          {isHindi ? ishtaDevata.note : devataEn?.note || ishtaDevata.note}
        </Typography>
      </Paper>

      <Alert severity="info">{labels.disclaimer}</Alert>
    </Container>
  );
};

export default ChakraAnalysis;
